// Utils
import * as pointsLib from '../../../common/maths/geometry/rect';
import { Rect } from '../../../common/maths/geometry/rect/rectTypes';

export type ViewportOptions = {
    excludeScrollbars?: boolean;
    insetMargins?: {
        top?: number;
        right?: number;
        bottom?: number;
        left?: number;
    };
};

/**
 * Will get the viewport rectangle of a scrollable element (the coordinates that are currently visible).
 * Scrollbars can optionally be included or excluded.
 *
 * NOTE: This will give coordinates from the start of the scrollable element.
 *      E.g. If the element hasn't been scrolled at all, top and left will be 0,0.
 */
export const getViewportRect = (
    scrollableParentNode: HTMLElement,
    {
        excludeScrollbars = true,
        // Margins to inset the viewport by.
        insetMargins,
    }: ViewportOptions = {},
): Rect => {
    // Just shortening the variable name
    const domNode = scrollableParentNode;

    const boundingRect = domNode.getBoundingClientRect();
    const {
        top: viewportTopInset = 0,
        right: viewportRightInset = 0,
        bottom: viewportBottomInset = 0,
        left: viewportLeftInset = 0,
    } = insetMargins || {};

    const verticalScrollbarWidth = (excludeScrollbars && domNode.offsetWidth - domNode.clientWidth) || 0;
    const horizontalScrollbarWidth = (excludeScrollbars && domNode.offsetHeight - domNode.clientHeight) || 0;

    const width = boundingRect.width - verticalScrollbarWidth - viewportLeftInset - viewportRightInset;
    const height = boundingRect.height - horizontalScrollbarWidth - viewportTopInset - viewportBottomInset;

    return {
        x: domNode.scrollLeft,
        y: domNode.scrollTop,
        top: domNode.scrollTop + viewportTopInset,
        left: domNode.scrollLeft + viewportLeftInset,
        bottom: domNode.scrollTop + viewportTopInset + height,
        right: domNode.scrollLeft + viewportLeftInset + width,

        width,
        height,
    };
};

/**
 * Translates a DOM rectangle into the same coordinate system as the scrollable element.
 * The DOMRect is from the window's top left, this will translate it to the scrollable element's coordinates.
 */
export const translateDOMRectIntoScrollableParentCoordinates = (scrollableParent: HTMLElement, rectangle: Rect) => {
    const scrollableParentRect = scrollableParent.getBoundingClientRect();

    // Translate to "scrollable area" coordinates (either the canvas or inbox).
    // I.e. top left will be 0,0
    const scrollableAreaTranslation = {
        // Current x scroll - current left
        x: scrollableParent.scrollLeft - scrollableParentRect.left,
        // Current y scroll - current top
        y: scrollableParent.scrollTop - scrollableParentRect.top,
    };

    return pointsLib.translate(scrollableAreaTranslation, rectangle);
};

export const canScrollX = (scrollableParent: HTMLElement) =>
    scrollableParent.scrollWidth !== scrollableParent.clientWidth;
export const canScrollY = (scrollableParent: HTMLElement) =>
    scrollableParent.scrollHeight !== scrollableParent.clientHeight;
export const canScroll = (scrollableParent: HTMLElement) =>
    canScrollX(scrollableParent) || canScrollY(scrollableParent);

// NOTE: the max helpers below deliberately bound by the content extent (scrollWidth/scrollHeight)
// rather than by the furthest the browser will actually scroll (scrollWidth - clientWidth), so a
// target past the current bounds yields an offset the browser refuses rather than one it accepts.
// snapIntoView's retry loop depends on that: it decides whether to keep waiting for a growing canvas
// by comparing the offset it assigned against the one the DOM took, so tightening these to the real
// maximum makes the first attempt look successful and the element never reaches its target once the
// canvas expands. See applyScrollTargetWhileBoundsGrow in animateScrollIntoView.ts.

// Can only scroll left as far as we've already scrolled right
export const getScrollXMin = (scrollableParent: HTMLElement) => -scrollableParent.scrollLeft;
export const getScrollXMax = (scrollableParent: HTMLElement) =>
    scrollableParent.scrollWidth - scrollableParent.scrollLeft;
// Can only scroll up as much as the scrollable element is scrolled down
export const getScrollYMin = (scrollableParent: HTMLElement) => -scrollableParent.scrollTop;
export const getScrollYMax = (scrollableParent: HTMLElement) =>
    scrollableParent.scrollHeight - scrollableParent.scrollTop;

/**
 * How far needs to be scrolled to make the left edge of the target touch the left edge of the viewport.
 */
const getDistToScrollToEdge =
    (edge: keyof Rect) =>
    ({ viewportRect, targetRect }: { viewportRect: Rect; targetRect: Rect }) =>
        targetRect[edge] - viewportRect[edge];
export const getDistToScrollToLeftEdge = getDistToScrollToEdge('left');
export const getDistToScrollToRightEdge = getDistToScrollToEdge('right');
export const getDistToScrollToTopEdge = getDistToScrollToEdge('top');
export const getDistToScrollToBottomEdge = getDistToScrollToEdge('bottom');

// Determine if the target is outside of the viewport
const getIsOverflowingLeft = ({ viewportRect, targetRect }: { viewportRect: Rect; targetRect: Rect }): boolean =>
    viewportRect.left > targetRect.left;
const getIsOverflowingRight = ({ viewportRect, targetRect }: { viewportRect: Rect; targetRect: Rect }): boolean =>
    viewportRect.right < targetRect.right;
const getIsOverflowingTop = ({ viewportRect, targetRect }: { viewportRect: Rect; targetRect: Rect }): boolean =>
    viewportRect.top > targetRect.top;
const getIsOverflowingBottom = ({ viewportRect, targetRect }: { viewportRect: Rect; targetRect: Rect }): boolean =>
    viewportRect.bottom < targetRect.bottom;

// Determine if the target is already at the extremities of the viewport
const targetIsOnViewportLeftEdge = ({ viewportRect, targetRect }: { viewportRect: Rect; targetRect: Rect }): boolean =>
    getDistToScrollToLeftEdge({ viewportRect, targetRect }) === 0;
const targetIsOnViewportTopEdge = ({ viewportRect, targetRect }: { viewportRect: Rect; targetRect: Rect }): boolean =>
    getDistToScrollToTopEdge({ viewportRect, targetRect }) === 0;

/**
 * If the target is overflowing the viewport to the left or right, we should scroll.
 */
const shouldScrollX = ({ viewportRect, targetRect }: { viewportRect: Rect; targetRect: Rect }): boolean =>
    getIsOverflowingLeft({ viewportRect, targetRect }) ||
    (getIsOverflowingRight({ viewportRect, targetRect }) &&
        // If the target is already exactly on the viewport left edge, we shouldn't scroll right
        !targetIsOnViewportLeftEdge({ viewportRect, targetRect }));

/**
 * If the target is overflowing the viewport to the left or right, we should scroll.
 */
const shouldScrollY = ({ viewportRect, targetRect }: { viewportRect: Rect; targetRect: Rect }): boolean =>
    getIsOverflowingTop({ viewportRect, targetRect }) ||
    (getIsOverflowingBottom({ viewportRect, targetRect }) &&
        // If the target is already exactly on the viewport left edge, we shouldn't scroll right
        !targetIsOnViewportTopEdge({ viewportRect, targetRect }));

/**
 * Determines whether scrolling should occur for a target rectangle within a scrollable element.
 */
export const shouldScroll = (
    scrollableParent: HTMLElement,
    targetRect: Rect,
    viewportOptions?: ViewportOptions,
): boolean => {
    const viewportRect = getViewportRect(scrollableParent, viewportOptions);

    return (
        (canScrollX(scrollableParent) && shouldScrollX({ viewportRect, targetRect })) ||
        (canScrollY(scrollableParent) && shouldScrollY({ viewportRect, targetRect }))
    );
};

/**
 * Gets the current scroll position of a DOM element as x, y coordinates.
 */
export const getScrollAsPoint = (element: HTMLElement): { x: number; y: number } =>
    element ? { x: element.scrollLeft, y: element.scrollTop } : { x: 0, y: 0 };

export const scrollToBottom = (domElement: HTMLElement): void => {
    if (!domElement || !canScrollY(domElement)) return;

    domElement.scrollTop = domElement.scrollHeight - domElement.clientHeight;
};
